// src/components/Pages/VideoLibraryPage.js

import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Container,
    Title,
    Text,
    useMantineTheme,
    useMantineColorScheme,
    Paper,
    Group,
    Table,
    Loader,
    Center,
    Alert,
    Stack,
    Pagination,
    Button,
    Tooltip,
    Badge,
    SegmentedControl,
    Modal,
    ActionIcon,
    CopyButton
} from '@mantine/core';
import {
    IconAlertCircle,
    IconVideo,
    IconClock,
    IconChecks,
    IconDownload,
    IconLoader,
    IconX,
    IconMessageCircle,
    IconCopy
} from '@tabler/icons-react';
import { getFirestore, collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';

import { useBackgroundImage } from '../../context/BackgroundImageContext';
import { useAuth } from '../../context/AuthContext';
import backgroundImageFile from '../../assets/images/brochure-background.jpg';

// --- HELPER: Status Indicator ---
const StatusIndicator = ({ status, theme }) => {
    const statusConfig = {
        queued: {
            icon: <IconClock size={16} />,
            label: 'Queued',
            color: theme.colors.gray[6],
            variant: 'outline'
        },
        processing: {
            icon: <IconLoader size={16} style={{ animation: 'spin 2s linear infinite' }} />,
            label: 'Processing',
            color: theme.colors.blue[6],
            variant: 'light'
        },
        completed: {
            icon: <IconChecks size={16} />,
            label: 'Completed',
            color: theme.colors.teal[6],
            variant: 'filled'
        },
        failed: {
            icon: <IconX size={16} />,
            label: 'Failed',
            color: theme.colors.red[6],
            variant: 'filled'
        }
    };

    // Default to processing if status is unrecognized (e.g., initial Orchestrator creation)
    const config = statusConfig[status?.toLowerCase()] || statusConfig['processing'];

    return (
        <Badge
            color={config.color}
            variant={config.variant}
            leftSection={config.icon}
            style={{ textTransform: 'uppercase' }}
        >
            {config.label}
        </Badge>
    );
};

// --- HELPER: Firebase Authenticated Download Button ---
const FirebaseDownloadButton = ({ storagePath, articleId }) => {
    const [url, setUrl] = useState(null);
    const [isFetching, setIsFetching] = useState(true);
    const [fetchError, setFetchError] = useState(false);

    useEffect(() => {
        if (!storagePath) {
            setIsFetching(false);
            setFetchError(true);
            return;
        }

        const fetchToken = async () => {
            try {
                const storage = getStorage();
                const fileRef = ref(storage, storagePath);
                // This is the "Zero Trust" token generation
                const downloadUrl = await getDownloadURL(fileRef);
                setUrl(downloadUrl);
            } catch (error) {
                console.error("Failed to fetch secure token for:", storagePath, error);
                setFetchError(true);
            } finally {
                setIsFetching(false);
            }
        };

        fetchToken();
    }, [storagePath]);

    if (isFetching) {
        return (
            <Button size="compact-sm" color="blue" variant="light" leftSection={<IconLoader size={14} style={{ animation: 'spin 2s linear infinite' }} />} disabled>
                Securing Link...
            </Button>
        );
    }

    if (fetchError || !url) {
        return (
            <Button size="compact-sm" color="red" variant="subtle" leftSection={<IconAlertCircle size={14} />} disabled>
                Access Denied
            </Button>
        );
    }

    return (
        <Button
            component="a"
            href={url}
            download={`LoveInc_Video_${articleId || 'Render'}.mp4`}
            target="_blank"
            size="compact-sm"
            color="teal"
            variant="outline"
            leftSection={<IconDownload size={14} />}
        >
            Download MP4
        </Button>
    );
};

// --- UTILITY: Dynamic Format Badge Routing ---
const getFormatBadgeConfig = (job) => {
    const rawId = job?.templateId || job?.inputProps?.compositionId || job?.compositionId || 'MSN Landscape';
    const cleanLabel = rawId.replace(/[-_]Composition/i, '').replace(/[-_]/g, ' ').trim();

    const colorMap = {
        'TikTok Portrait': 'red.9',      
        'LoveInc Landscape': 'grape',   
        'MSN Landscape': 'blue',        
        'MSN Portrait': 'teal'        
    };

    return {
        label: cleanLabel,
        color: colorMap[cleanLabel] || 'white' // violet acts as our fallback
    };
};

function VideoLibraryPage() {
    const theme = useMantineTheme();
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === 'dark';
    const { isBackgroundImageVisible } = useBackgroundImage();
    const headerTextColor = isBackgroundImageVisible ? theme.white : undefined;
    const { currentUser } = useAuth();

    // State
    const [jobs, setJobs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activePage, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    // UI State
    const [jobForSocialCopy, setJobForSocialCopy] = useState(null);

    // --- REAL-TIME ENGINE (Firestore Snapshot) ---
    useEffect(() => {
        if (!currentUser) return;

        const db = getFirestore();
        const jobsRef = collection(db, 'video_render_jobs');
        // We order by creation time so the newest jobs are always at the top
        const q = query(jobsRef, orderBy('createdAt', 'desc'));

        setIsLoading(true);

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const fetchedJobs = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            setJobs(fetchedJobs);
            setIsLoading(false);
            setError(null);
        }, (err) => {
            console.error("Snapshot error:", err);
            setError("Failed to sync video library data. Check your permissions.");
            setIsLoading(false);
        });

        // Cleanup listener on unmount
        return () => unsubscribe();
    }, [currentUser]);

    // --- HANDLERS ---
    const handleRowsPerPageChange = (value) => {
        setRowsPerPage(parseInt(value, 10));
        setPage(1); // Reset to first page
    };

    const formatDate = (timestamp) => {
        if (!timestamp || !timestamp.toDate) return 'N/A';
        const date = timestamp.toDate();
        return date.toLocaleString('en-GB', {
            day: 'numeric', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    // --- PAGINATION LOGIC ---
    const totalResults = jobs.length;
    const totalPages = Math.ceil(totalResults / rowsPerPage);
    const start = (activePage - 1) * rowsPerPage;
    const end = start + rowsPerPage;
    const paginatedJobs = jobs.slice(start, end);

    // --- RENDER CONTENT ---
    const renderContent = () => {
        if (isLoading && jobs.length === 0) {
            return <Center mt="xl"><Loader /><Text ml="md">Syncing Video Library...</Text></Center>;
        }
        if (error) {
            return <Alert icon={<IconAlertCircle size={16} />} title="Error!" color="red" mt="xl">{error}</Alert>;
        }

        return (
            <Stack>
                <Paper withBorder shadow="md" p="md" mt="xl" radius="md">
                    <Group justify="space-between" align="center" mb="md" style={{ minHeight: '36px' }}>
                        <Box>
                            <Text size="sm" c="dimmed">
                                Showing {totalResults > 0 ? start + 1 : 0} - {Math.min(end, totalResults)} of {totalResults} Renders
                            </Text>
                        </Box>

                        {totalResults > 0 && (
                            <Group>
                                <Text size="sm" c="dimmed">Rows:</Text>
                                <SegmentedControl
                                    value={String(rowsPerPage)}
                                    onChange={handleRowsPerPageChange}
                                    data={[
                                        { label: '10', value: '10' },
                                        { label: '25', value: '25' },
                                        { label: '50', value: '50' }
                                    ]}
                                    size="xs"
                                />
                                <Pagination total={totalPages} value={activePage} onChange={setPage} withEdges />
                            </Group>
                        )}
                    </Group>

                    <Table.ScrollContainer minWidth={1200}>
                        <Table striped highlightOnHover verticalSpacing="sm">
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th style={{ width: '15%' }}>Initiated (UTC)</Table.Th>
                                    <Table.Th c="dimmed" style={{ width: '10%' }}>Job ID</Table.Th>
                                    <Table.Th style={{ width: '40%' }}>Article-to-Video Context</Table.Th>
                                    <Table.Th style={{ textAlign: 'center', width: '15%' }}>Status</Table.Th>
                                    <Table.Th style={{ textAlign: 'right', width: '15%' }}>Actions</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {paginatedJobs.map((job) => (
                                    <Table.Tr key={job.id}>
                                        <Table.Td>{formatDate(job.createdAt)}</Table.Td>
                                        <Table.Td>
                                            <Tooltip label={`Full ID: ${job.id}`} withArrow>
                                                <Text size="xs" c="dimmed" style={{ cursor: 'help' }}>
                                                    {/* job.id.substring(0, 8)*/}
                                                    {job.id}
                                                </Text>
                                            </Tooltip>
                                        </Table.Td>
                                        <Table.Td>
                                            {/* 1. AI Video Title (Primary) */}
                                            <Text
                                                size="sm"
                                                fw={700}
                                                c={isDark ? theme.colors.blue[4] : theme.colors.blue[8]}
                                                lineClamp={1}
                                                title={job.inputProps?.timeline?.[0]?.text || 'Untitled Video'}
                                            >
                                                {job.inputProps?.timeline?.[0]?.text || 'Untitled Video'}
                                            </Text>

                                            {/* 2. Original Article Title (Secondary) */}
                                            <Text
                                                size="xs"
                                                fw={500}
                                                lineClamp={1}
                                                title={job.inputProps?.meta?.title || job.title || 'Unknown Source Article'}
                                            >
                                                FROM: {job.inputProps?.meta?.title || job.title || 'Unknown Source Article'}
                                            </Text>

                                           {/* 3. Metadata (Tertiary) */}
                                            <Text component="div" size="sm" c="dimmed" mt={2} style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                                Brand: <Badge size="sm" variant="outline" color="gray" radius="sm" style={{ textTransform: 'uppercase' }}>{job.inputProps?.meta?.brand || 'Unknown'}</Badge>
                                                | Format: <Badge size="sm" variant="filled" color={getFormatBadgeConfig(job).color} radius="sm">{getFormatBadgeConfig(job).label}</Badge>
                                                | Gallery: {job.articleId || 'N/A'}
                                            </Text>
                                        </Table.Td>
                                        <Table.Td style={{ textAlign: 'center' }}>
                                            <StatusIndicator status={job.status} theme={theme} />
                                        </Table.Td>
                                        <Table.Td style={{ textAlign: 'right' }}>
                                            <Group gap={8} justify="flex-end" wrap="nowrap">
                                                {/* NEW SOCIAL COPY BUTTON */}
                                                {(job.inputProps?.meta?.distribution_metadata?.caption || job.meta?.distribution_metadata?.caption || job.distribution_metadata?.caption) && (
                                                    <Tooltip label="View Social Copy" withArrow>
                                                        <ActionIcon 
                                                            variant="light" 
                                                            color="white" 
                                                            size="md" 
                                                            onClick={() => setJobForSocialCopy(job)}
                                                        >
                                                            <IconMessageCircle size={18} />
                                                        </ActionIcon>
                                                    </Tooltip>
                                                )}

                                                {/* EXISTING DOWNLOAD LOGIC */}
                                                {job.status === 'completed' ? (
                                                    job.storagePath ? (
                                                        <FirebaseDownloadButton
                                                            storagePath={job.storagePath}
                                                            articleId={job.articleId}
                                                        />
                                                    ) : (
                                                        <Button size="compact-sm" color="red" variant="subtle" disabled>
                                                            Path Missing
                                                        </Button>
                                                    )
                                                ) : job.status === 'failed' ? (
                                                    <Button size="compact-sm" color="red" variant="subtle" disabled>
                                                        Render Failed
                                                    </Button>
                                                ) : (
                                                    <Button size="compact-sm" color="gray" variant="subtle" disabled>
                                                        Awaiting File
                                                    </Button>
                                                )}
                                            </Group>
                                        </Table.Td>
                                    </Table.Tr>
                                ))}
                            </Table.Tbody>
                        </Table>
                    </Table.ScrollContainer>

                    {jobs.length === 0 && !isLoading && (
                        <Text ta="center" py="xl" c="dimmed">No render jobs found in the library.</Text>
                    )}
                </Paper>
            </Stack>
        );
    };

    return (
        <Box
            style={{
                width: '100%',
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                backgroundImage: isBackgroundImageVisible ? `url(${backgroundImageFile})` : 'none',
                backgroundColor: !isBackgroundImageVisible ? (isDark ? theme.colors.dark[7] : theme.colors.gray[0]) : 'transparent',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundAttachment: 'fixed',
                overflowY: 'scroll'
            }}
        >
            <Container size="xl" pt="xl">
                <Title order={1} ta="center" mb="xs" style={{ color: headerTextColor }}>
                    <IconVideo color={theme.colors.blue[isDark || isBackgroundImageVisible ? 4 : 6]} style={{ marginRight: theme.spacing.sm, verticalAlign: 'middle' }} />
                    Video Library
                </Title>
                <Text size="xl" ta="center" mb="xl" style={{ color: headerTextColor }}>
                    Access and download your finalised high-fidelity MP4 renders.
                </Text>

                <Container fluid px={0} pb="xl">
                    {renderContent()}
                </Container>
            </Container>

            {/* --- MODAL: SOCIAL MEDIA COPY --- */}
            <Modal 
                opened={!!jobForSocialCopy} 
                onClose={() => setJobForSocialCopy(null)} 
                title={<Text fw={600}>Social Media Copy</Text>}
                centered
                size="lg"
            >
                {jobForSocialCopy && (() => {
                    const meta = jobForSocialCopy.inputProps?.meta?.distribution_metadata 
                              || jobForSocialCopy.meta?.distribution_metadata 
                              || jobForSocialCopy.distribution_metadata;
                    const caption = meta?.caption || '';
                    const tags = (meta?.hashtags || []).join(' ');
                    const fullText = `${caption}\n\n${tags}`.trim();

                    return (
                        <>
                            <Paper withBorder p="md" radius="md" style={{ backgroundColor: isDark ? theme.colors.dark[6] : theme.colors.gray[0] }}>
                                <Text size="sm" style={{ whiteSpace: 'pre-wrap' }} mb="md">
                                    {caption}
                                </Text>
                                <Text size="sm" fw={600} c={isDark ? theme.colors.blue[4] : theme.colors.blue[7]}>
                                    {tags}
                                </Text>
                            </Paper>

                            <Group justify="flex-end" mt="md">
                                <Button variant="default" onClick={() => setJobForSocialCopy(null)}>Close</Button>
                                <CopyButton value={fullText} timeout={2000}>
                                    {({ copied, copy }) => (
                                        <Button 
                                            color={copied ? 'teal' : 'blue'} 
                                            onClick={copy} 
                                            leftSection={copied ? <IconChecks size={16} /> : <IconCopy size={16} />}
                                        >
                                            {copied ? 'Copied to Clipboard' : 'Copy Full Text'}
                                        </Button>
                                    )}
                                </CopyButton>
                            </Group>
                        </>
                    );
                })()}
            </Modal>
        </Box>
    );
}

export default VideoLibraryPage;